/**
 * AgentContactsTab - Gerenciamento de contatos do agente.
 *
 * Lista de pessoas para quem o agente pode encaminhar mensagens via WhatsApp.
 * Os contatos ficam disponíveis para qualquer capability que tenha a tool
 * send_whatsapp_to_contact (ex: Leitor de Email, futuras capabilities).
 */

import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert } from 'react-bootstrap';
import NumberFormat from 'react-number-format';
import {
  IconPlus,
  IconTrash,
  IconAddressBook,
} from '@tabler/icons-react';
import { useAgentsFlow } from '../../contexts/AgentsFlowContext';

const formatPhoneMask = (value) => {
  const clean = value.replace(/\D/g, '');
  if (clean.length === 0) return '';
  if (clean.length <= 2) return `(${clean}`;
  if (clean.length <= 6) return clean.replace(/(\d{2})(\d{0,4})/, '($1) $2');
  if (clean.length <= 10) return clean.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
  return clean.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
};

const AgentContactsTab = () => {
  const { state, updateAgentContacts } = useAgentsFlow();
  const editor = state.editor || {};
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    if (editor?.agent_contacts) {
      setContacts([...editor.agent_contacts]);
    }
  }, [editor?.agent_contacts]);

  const handleUpdate = (newContacts) => {
    setContacts(newContacts);
    updateAgentContacts(newContacts);
  };

  const addContact = () => {
    handleUpdate([...contacts, { name: '', phone: '', role: '' }]);
  };

  const removeContact = (index) => {
    handleUpdate(contacts.filter((_, i) => i !== index));
  };

  const updateContact = (index, field, value) => {
    const updated = contacts.map((contact, i) =>
      i === index ? { ...contact, [field]: value } : contact
    );
    handleUpdate(updated);
  };

  return (
    <div className="p-3">
      <Card className="border-0 shadow-sm mb-3">
        <Card.Body>
          <div className="d-flex align-items-center gap-2 mb-3">
            <IconAddressBook size={20} />
            <h6 className="mb-0">Contatos do Agente</h6>
          </div>

          <p className="text-muted mb-3">
            Cadastre pessoas para quem o agente pode enviar mensagens via WhatsApp.
            Ao conversar, basta dizer "envia pro(a) [nome]" e o agente resolve o número automaticamente.
          </p>

          {contacts.length === 0 && (
            <Alert variant="info" className="py-2">
              Nenhum contato cadastrado. Adicione contatos para que o agente possa encaminhar
              mensagens e resumos via WhatsApp.
            </Alert>
          )}

          {contacts.map((contact, index) => (
            <Card key={index} className="mb-2 border">
              <Card.Body className="py-2 px-3">
                <div className="d-flex align-items-start gap-2">
                  <div className="flex-grow-1">
                    <div className="d-flex gap-2 mb-2">
                      <Form.Group className="flex-grow-1">
                        <Form.Label>Nome</Form.Label>
                        <Form.Control
                          type="text"
                          size="sm"
                          value={contact.name || ''}
                          onChange={(e) => updateContact(index, 'name', e.target.value)}
                          placeholder="Ex: Camila"
                        />
                      </Form.Group>
                      <Form.Group className="w-40">
                        <Form.Label>Função</Form.Label>
                        <Form.Control
                          type="text"
                          size="sm"
                          value={contact.role || ''}
                          onChange={(e) => updateContact(index, 'role', e.target.value)}
                          placeholder="Ex: Gerente"
                        />
                      </Form.Group>
                    </div>
                    <Form.Group>
                      <Form.Label>WhatsApp</Form.Label>
                      <NumberFormat
                        className="form-control form-control-sm"
                        format={formatPhoneMask}
                        mask="_"
                        placeholder="(00) 00000-0000"
                        value={contact.phone || ''}
                        onValueChange={(values) => updateContact(index, 'phone', values.value)}
                      />
                    </Form.Group>
                  </div>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => removeContact(index)}
                    className="mt-4"
                  >
                    <IconTrash size={14} />
                  </Button>
                </div>
              </Card.Body>
            </Card>
          ))}

          <Button
            variant="outline-primary"
            size="sm"
            onClick={addContact}
            className="mt-2"
          >
            <IconPlus size={14} className="me-1" />
            Adicionar Contato
          </Button>

          {contacts.length > 0 && (
            <div className="mt-3 p-2 rounded text-muted small">
              <strong>Como usar:</strong> Em qualquer conversa com capacidades como Leitor de Email,
              o usuário pode dizer "envia resumo pra {contacts[0]?.name || 'Camila'}" e o agente
              envia automaticamente via WhatsApp.
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default AgentContactsTab;
